<template>
  <div>
    <template v-if="isWide">
      <RainWideLayer :boundsList="boundsList" />
    </template>
  </div>
</template>
<script>
import axios from "axios";
import moment from "moment";
import L from "leaflet";
import RainWideLayer from "./RainWideLayer";

export default {
  name: "KouzuiKikikuruLayer",
  components: {
    RainWideLayer
  },
  data() {
    return {
      tiles: [],
      boundsList: [],
      currentTileBounds: null,
      currentBaseDate: null,
      gridLayer: null,
      syncRequestId: 0,
      refreshTimer: null
    };
  },
  computed: {
    map() {
      return this.$store.getters.leafletMap;
    },
    opacity() {
      return this.$store.getters.riskSubControl.opacity;
    },
    selectedDate() {
      return this.$store.getters.selectedDate;
    },
    baseDate() {
      const baseDate = this.$store.getters.baseDate.clone();
      baseDate.subtract(baseDate.minute() % 5, "minutes");
      return baseDate.utc().format("YYYY/MM/DD HH:mm");
    },
    timeSeries() {
      return this.$store.getters.rainImageInfo;
    },
    selectedRain() {
      const d = this.selectedDate.clone().utc();
      const date = d.format("YYYY/MM/DD HH:mm");
      return this.timeSeries.find(r => r.date === date);
    },
    tileUrl() {
      if (this.isWide === true) {
        return "";
      }
      return this.selectedRain ? this.selectedRain.tileUrl : "";
    },
    zoom() {
      return Math.floor(this.map.getZoom());
    },
    isWide() {
      return this.zoom <= 9;
    }
  },
  watch: {
    tileUrl() {
      this.refreshGridLayer();
    },
    opacity() {
      if (this.gridLayer) {
        this.gridLayer.setOpacity(this.opacity);
      }
    },
    tiles: {
      deep: true,
      handler() {
        this.refreshGridLayer();
      }
    },
    baseDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentBaseDate = null;
        this.sync();
      }
    }
  },
  async created() {
    await this.sync();
    this.map.on("moveend", this.mapMoveend);
  },
  mounted() {
    this.gridLayer = this.createGridLayer();
    this.map.addLayer(this.gridLayer);
  },
  beforeDestroy() {
    this.map.off("moveend", this.mapMoveend);
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
    }
    if (this.gridLayer) {
      this.map.removeLayer(this.gridLayer);
    }
  },
  methods: {
    createGridLayer() {
      const component = this;
      const GridLayer = L.GridLayer.extend({
        createTile(coords, done) {
          const canvas = L.DomUtil.create("canvas", "leaflet-tile");
          const tileSize = this.getTileSize();
          const origin = L.point(coords.x * tileSize.x, coords.y * tileSize.y);
          const tileUrl = component.tileUrl;
          const gridNorthwest = component.map.unproject(origin, coords.z);
          const gridSoutheast = component.map.unproject(
            origin.add([tileSize.x, tileSize.y]),
            coords.z
          );
          const gridBounds = L.latLngBounds(gridNorthwest, gridSoutheast);
          const images = tileUrl
            ? component.tiles
                .map(tile => ({
                  tile,
                  url: tileUrl.replace("{code}", tile.code)
                }))
                .filter(({ tile }) => {
                  const south = Math.min(tile.bounds[0][0], tile.bounds[1][0]);
                  const north = Math.max(tile.bounds[0][0], tile.bounds[1][0]);
                  const west = Math.min(tile.bounds[0][1], tile.bounds[1][1]);
                  const east = Math.max(tile.bounds[0][1], tile.bounds[1][1]);
                  return gridBounds.intersects(
                    L.latLngBounds([south, west], [north, east])
                  );
                })
            : [];
          const context = canvas.getContext("2d");
          let remaining = images.length;
          const pixelRatio = window.devicePixelRatio || 1;

          canvas.width = tileSize.x * pixelRatio;
          canvas.height = tileSize.y * pixelRatio;
          canvas.style.width = `${tileSize.x}px`;
          canvas.style.height = `${tileSize.y}px`;
          canvas.style.opacity = component.opacity;
          context.scale(pixelRatio, pixelRatio);
          context.imageSmoothingEnabled = false;

          const finish = () => {
            remaining -= 1;
            if (remaining === 0) {
              done(null, canvas);
            }
          };

          if (remaining === 0) {
            done(null, canvas);
            return canvas;
          }

          images.forEach(({ tile, url }) => {
            const image = new Image();
            image.onload = () => {
              const south = Math.min(tile.bounds[0][0], tile.bounds[1][0]);
              const north = Math.max(tile.bounds[0][0], tile.bounds[1][0]);
              const west = Math.min(tile.bounds[0][1], tile.bounds[1][1]);
              const east = Math.max(tile.bounds[0][1], tile.bounds[1][1]);
              const northwest = component.map.project([north, west], coords.z);
              const southeast = component.map.project([south, east], coords.z);
              context.drawImage(
                image,
                northwest.x - origin.x,
                northwest.y - origin.y,
                southeast.x - northwest.x,
                southeast.y - northwest.y
              );
              finish();
            };
            image.onerror = finish;
            image.src = url;
          });

          return canvas;
        }
      });
      return new GridLayer({
        tileSize: 256,
        opacity: this.opacity,
        zIndex: 400,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 2
      });
    },
    refreshGridLayer() {
      if (this.refreshTimer !== null) {
        clearTimeout(this.refreshTimer);
      }
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        if (this.gridLayer) {
          this.gridLayer.redraw();
        }
      }, 100);
    },
    async mapMoveend() {
      if (this.currentTileBounds !== null) {
        const contained = this.currentTileBounds.contains(this.map.getBounds());
        if (contained === true) {
          return;
        }
      }
      if (this.zoom > 9) {
        this.sync();
      }
    },
    async sync() {
      const requestId = ++this.syncRequestId;
      const northwest = this.map.getBounds().getNorthWest();
      const southeast = this.map.getBounds().getSouthEast();

      const params = {
        northwest_lat: northwest.lat,
        northwest_lng: northwest.lng,
        southeast_lat: southeast.lat,
        southeast_lng: southeast.lng,
        date: this.baseDate
      };
      const url = "https://data.riskma.net/bosai/inundation-kikikuru/flood";
      const response = await axios.get(url, {
        params
      });
      if (requestId !== this.syncRequestId) {
        return;
      }
      if (response === null) {
        this.$store.commit("SET_RAIN_IMAGE_INFO", []);
        return;
      }
      this.boundsList = response.data.bounds;
      if (this.isWide === false) {
        if (this.tiles !== response.data.tiles) {
          this.tiles = response.data.tiles;
        }
      } else {
        this.tiles = [];
      }
      this.loadCurrentTileBounds(northwest, southeast);

      // download wide urls
      const rainImageInfo = response.data.timeSeries.map(r => {
        const image = new Image();
        image.src = r.url;
        r.image = image;
        return r;
      });
      const responseBaseDate = moment.utc(
        response.data.baseDate,
        "YYYY/MM/DD HH:mm"
      );
      this.$store.commit("SET_RISK_BASE_DATE", responseBaseDate.clone());
      if (this.currentBaseDate === null) {
        this.currentBaseDate = responseBaseDate;
        this.$store.commit("SET_SELECTED_DATE", responseBaseDate.clone());
      }
      this.$store.commit("SET_RAIN_IMAGE_INFO", rainImageInfo);
    },
    loadCurrentTileBounds(queryNorthwest, querySoutheast) {
      if (this.tiles.length === 0) {
        this.currentTileBounds = null;
        return;
      }
      const northwestLat = this.tiles.map(t => t.bounds[0][0]);
      const northwestLng = this.tiles.map(t => t.bounds[0][1]);
      const southeastLat = this.tiles.map(t => t.bounds[1][0]);
      const southeastLng = this.tiles.map(t => t.bounds[1][1]);
      northwestLat.push(queryNorthwest.lat);
      northwestLng.push(queryNorthwest.lng);
      southeastLat.push(querySoutheast.lat);
      southeastLng.push(querySoutheast.lng);

      const northwest = L.latLng([
        Math.max(...northwestLat),
        Math.min(...northwestLng)
      ]);
      const southeast = L.latLng([
        Math.min(...southeastLat),
        Math.max(...southeastLng)
      ]);
      this.currentTileBounds = L.latLngBounds(northwest, southeast);
    }
  }
};
</script>
